import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  Shield, 
  Badge, 
  Building, 
  Users, 
  ArrowRight, 
  CheckCircle, 
  Zap,
  Lock,
  Eye,
  Brain,
  Database,
  Globe,
  TrendingUp,
  AlertTriangle,
  Cpu,
  Wifi,
  Fingerprint,
  Satellite,
  Radar,
  Target,
  FileText,
  Scale,
  Gavel,
  Factory,
  Truck,
  MapPin,
  Clock,
  BarChart3,
  Network,
  Server,
  Key,
  Award,
  Star,
  Rocket,
  Zap as Lightning,
  Globe as World,
  Shield as Security,
  Cpu as AI,
  Database as Blockchain,
  Wifi as IoT
} from 'lucide-react';
import { Link } from 'react-router-dom';

const MarketingHomepage = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const { scrollY } = useScroll();
  
  const y1 = useTransform(scrollY, [0, 300], [0, 100]);
  const y2 = useTransform(scrollY, [0, 300], [0, -100]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0.3]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Removed fake stats - will be replaced with real data from backend
  const stats = [];

  const features = [
    {
      icon: Shield,
      title: 'Military-Grade Security',
      description: 'CJIS, FedRAMP, and military-grade compliance with zero-trust architecture',
      color: 'from-red-500 to-red-700'
    },
    {
      icon: Brain,
      title: 'AI-Powered Compliance',
      description: 'Advanced machine learning algorithms that automatically detect and prevent compliance violations',
      color: 'from-blue-500 to-blue-700'
    },
    {
      icon: Blockchain,
      title: 'Blockchain Chain of Custody',
      description: 'Immutable tracking of every firearm from manufacturer to end user with real-time verification',
      color: 'from-green-500 to-green-700'
    },
    {
      icon: IoT,
      title: 'IoT Integration',
      description: 'Real-time monitoring of connected devices with instant alerts and automated responses',
      color: 'from-purple-500 to-purple-700'
    },
    {
      icon: Radar,
      title: 'Threat Intelligence',
      description: 'Predictive analytics that identify potential security threats before they materialize',
      color: 'from-orange-500 to-orange-700'
    },
    {
      icon: Scale,
      title: 'Regulatory Compliance',
      description: 'Automated compliance reporting for ATF, CJIS, and international regulations',
      color: 'from-pink-500 to-pink-700'
    }
  ];

  const useCases = [
    {
      title: 'Law Enforcement',
      description: 'Real-time tracking and compliance for police departments and federal agencies',
      icon: Badge,
      color: 'from-red-500 to-red-700',
      features: ['CJIS Compliance', 'Real-time Monitoring', 'Threat Detection']
    },
    {
      title: 'Military Bases',
      description: 'Secure weapon tracking and access control for military installations worldwide',
      icon: Shield,
      color: 'from-blue-500 to-blue-700',
      features: ['FedRAMP Security', 'Access Control', 'Audit Logging']
    },
    {
      title: 'FFL Dealers',
      description: 'Automated compliance and inventory management for firearms dealers',
      icon: Users,
      color: 'from-green-500 to-green-700',
      features: ['ATF Compliance', 'Inventory Tracking', 'Automated Reporting']
    },
    {
      title: 'Manufacturers',
      description: 'End-to-end tracking from production to distribution with quality control',
      icon: Factory,
      color: 'from-purple-500 to-purple-700',
      features: ['Quality Control', 'Supply Chain', 'Compliance Automation']
    }
  ];

  // Removed fake testimonials - will be replaced with real testimonials when available
  const testimonials = [];

  return (
    <div className="min-h-screen bg-gray-900 overflow-hidden">
      {/* Header with Login Button */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gray-900/80 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="/FullLogo_Transparent_NoBuffer.png" 
                alt="Dark Matter Solutions" 
                className="h-8 w-auto"
              />
            </Link>
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
              >
                Log In
              </Link>
              <Link
                to="/register"
                className="px-6 py-2 border border-gray-600 hover:border-blue-500 text-gray-300 hover:text-blue-400 font-semibold rounded-lg transition-colors"
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Animated Background */}
      <div className="fixed inset-0 opacity-20">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-red-900/20" />
        <div className="absolute top-0 left-0 w-full h-full">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-blue-400 rounded-full"
              animate={{
                x: [0, 100, 0],
                y: [0, 100, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: Math.random() * 15 + 15,
                repeat: Infinity,
                delay: Math.random() * 10,
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center pt-16">
        <motion.div style={{ y: y1, opacity }} className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
        </motion.div>
        
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8">
          {/* Logo and Brand */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, type: "spring" }}
            className="mb-8"
          >
            <div className="flex justify-center mb-6">
              <img 
                src="/FullLogo_Transparent_NoBuffer.png" 
                alt="Dark Matter Solutions" 
                className="h-32 w-auto"
              />
            </div>
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
              <span className="text-green-400 text-sm font-mono">SYSTEM ONLINE</span>
            </div>
          </motion.div>


          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="text-2xl md:text-3xl text-gray-300 mb-8 max-w-5xl mx-auto font-light"
          >
            The world's most advanced AI-powered firearm compliance and security platform
          </motion.p>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="text-lg text-gray-400 mb-12 max-w-4xl mx-auto"
          >
            Professional firearms compliance and security platform for FFL dealers and manufacturers. 
            Combining cutting-edge AI, blockchain technology, and military-grade security to deliver 
            unprecedented compliance automation and threat prevention.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16"
          >
            <Link
              to="/login"
              className="group relative px-10 py-5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl text-lg"
            >
              <span className="flex items-center">
                Get Started
                <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
            
            <Link
              to="/register"
              className="px-10 py-5 border-2 border-gray-600 text-gray-300 hover:border-blue-500 hover:text-blue-400 font-bold rounded-xl transition-all duration-300 transform hover:scale-105 text-lg"
            >
              Create Account
            </Link>
            

            <a
              href="/mobile.html"
              target="_blank"
              className="px-10 py-5 border-2 border-gray-600 text-gray-300 hover:border-green-500 hover:text-green-400 font-bold rounded-xl transition-all duration-300 transform hover:scale-105 text-lg inline-block"
            >
              📱 Mobile App
            </a>

            <button className="px-10 py-5 border-2 border-gray-600 text-gray-300 hover:border-purple-500 hover:text-purple-400 font-bold rounded-xl transition-all duration-300 transform hover:scale-105 text-lg">
              Contact Sales
            </button>
          </motion.div>

          {/* Stats - Removed fake stats, will show real metrics when available */}
        </div>

        {/* Floating Elements */}
        <motion.div
          style={{ y: y2 }}
          className="absolute top-20 right-20 hidden lg:block"
        >
          <div className="w-40 h-40 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur-3xl" />
        </motion.div>
        
        <motion.div
          style={{ y: y1 }}
          className="absolute bottom-20 left-20 hidden lg:block"
        >
          <div className="w-48 h-48 bg-gradient-to-r from-purple-500/20 to-red-500/20 rounded-full blur-3xl" />
        </motion.div>
      </div>

      {/* Features Section */}
      <div className="relative py-24 bg-gray-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Revolutionary <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Technology</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Cutting-edge features that set Dark Matter Solutions apart from traditional compliance platforms
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border border-gray-700 hover:border-blue-500/50 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl h-full">
                  <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 text-center">{feature.title}</h3>
                  <p className="text-gray-300 leading-relaxed text-center">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Use Cases */}
      <div className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Trusted by <span className="bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">Industry Leaders</span>
            </h2>
            <p className="text-xl text-gray-400">
              Serving diverse sectors with tailored compliance solutions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border border-gray-700 hover:border-blue-500/50 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl h-full">
                  <div className={`w-20 h-20 bg-gradient-to-r ${useCase.color} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform`}>
                    <useCase.icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{useCase.title}</h3>
                  <p className="text-gray-300 mb-6 text-sm leading-relaxed">{useCase.description}</p>
                  <div className="space-y-2">
                    {useCase.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-400">
                        <CheckCircle className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" />
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials - Removed fake testimonials, will show real testimonials when available */}

      {/* CTA Section */}
      <div className="py-24 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Compliance</span>?
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
              Get started with Dark Matter Solutions to automate compliance, 
              enhance security, and prevent threats in real-time.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link
              to="/login"
              className="group relative px-12 py-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl text-xl"
            >
              <span className="flex items-center">
                Log In
                <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
            <Link
              to="/register"
              className="px-12 py-6 border-2 border-gray-600 text-gray-300 hover:border-blue-500 hover:text-blue-400 font-bold rounded-xl transition-all duration-300 transform hover:scale-105 text-xl"
            >
              Create Account
            </Link>
              
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-12 bg-gray-900 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
            <span className="text-green-400 text-sm font-mono">
              {currentTime.toLocaleTimeString()} • ENTERPRISE OPERATIONAL
            </span>
          </div>
          <p className="text-gray-400 mb-4">
            © 2024 Dark Matter Solutions. The world's most advanced AI-powered compliance platform.
          </p>
          <div className="flex justify-center space-x-6 text-sm text-gray-500">
            <span>Privacy Policy</span>
            <span>Terms of Service</span>
            <span>Security</span>
            <span>Support</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingHomepage;
